import api from '../config/api';

/**
 * Fetch users with pagination and optional filters.
 * @param {Object} params
 * @param {number} params.page
 * @param {number} params.limit
 * @param {string} [params.search]
 * @param {string} [params.role]
 * @param {string} [params.status]
 * @returns {Promise<Object>} Normalized API response data
 */
export const getUsers = async (params = {}) => {
  try {
    const {
      page = 1,
      limit = 10,
      search,
      role,
      status,
    } = params;

    const offset = (page - 1) * limit;

    const queryParams = {
      limit,
      offset,
    };

    if (search) queryParams.search = search;
    if (role) queryParams.role = role;
    if (status) queryParams.status = status;

    const response = await api.get('/api/users', { params: queryParams });
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to fetch users. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new user
 * @param {Object} userData
 * @returns {Promise<Object>}
 */
export const createUser = async (userData) => {
  try {
    const response = await api.post('/api/users', userData);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to create user. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update an existing user
 * @param {number|string} id
 * @param {Object} userData
 * @returns {Promise<Object>}
 */
export const updateUser = async (id, userData) => {
  try {
    const response = await api.put(`/api/users/${id}`, userData);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to update user. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete a user
 * @param {number|string} id
 * @returns {Promise<Object>}
 */
export const deleteUser = async (id) => {
  try {
    const response = await api.delete(`/api/users/${id}`);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to delete user. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Export users - try server endpoint, fallback to fetching all users
 * @returns {Promise<Array>}
 */
export const exportUsers = async () => {
  try {
    // Try dedicated export endpoint first
    const resp = await api.get('/api/users/export', { responseType: 'blob' });
    // If server returns a blob/csv, return as-is (caller will handle)
    if (resp && resp.data) return resp.data;
    // Fallback - fetch all users
    const all = await getUsers({ page: 1, limit: 1000 });
    return all;
  } catch (error) {
    // Fallback to fetching all users if export endpoint unavailable
    try {
      const all = await getUsers({ page: 1, limit: 1000 });
      return all;
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to export users. Please try again.';
      throw new Error(errorMessage);
    }
  }
};
